import type { AppDirectClient } from '../client.js';
import { AuthError } from '../errors.js';
import { refreshSession } from '../flows/refresh.js';
import { isJwtExpired } from './jwt.js';
import type { RefreshCoordinator } from './refresh-coordinator.js';
import type { JwtResult, ResolvedConfig } from '../types.js';

async function coordinatedRefresh(
  config: ResolvedConfig,
  client: AppDirectClient,
  coordinator: RefreshCoordinator,
  refreshToken: string,
): Promise<JwtResult> {
  try {
    const refreshed = await refreshSession(config, client, coordinator, { refreshToken });

    return {
      token: refreshed.fejwt,
      cookiesToSet: refreshed.cookiesToSet,
    };
  } catch (err) {
    if (err instanceof AuthError && (err.code === 'fejwt' || err.code === 'refresh_failed')) {
      throw new AuthError('session_expired', 'Session expired');
    }
    throw err;
  }
}

export async function ensureValidFeJwt(
  config: ResolvedConfig,
  client: AppDirectClient,
  coordinator: RefreshCoordinator,
  input: { sessionToken: string | null; refreshToken: string | null },
): Promise<JwtResult> {
  const sessionToken = input.sessionToken ?? null;
  const refreshToken = input.refreshToken ?? null;

  if (!sessionToken) {
    if (!refreshToken) {
      throw new AuthError('not_authenticated', 'Not authenticated');
    }
    return coordinatedRefresh(config, client, coordinator, refreshToken);
  }

  if (!isJwtExpired(sessionToken, config.expiryBufferSec)) {
    return { token: sessionToken };
  }

  if (!refreshToken) {
    throw new AuthError('session_expired', 'Session expired');
  }

  return coordinatedRefresh(config, client, coordinator, refreshToken);
}
